import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';

import {
	isProcessReadyToSchedule,
	isSkipped,
	setupCardItems,
} from '../../../../bridge/utils/setupCardItems.ts';

export const useGoToNextStep = () => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();

	return useCallback(
		(processId: string, justCompletedItemId: string): void => {
			const snapshot =
				bridgeApi.user_state?.process_setup_snapshots?.[processId];
			if (!snapshot) {
				bridgeApi.sendAction({ type: 'do_close_chat_panel' });
				return;
			}

			const items = setupCardItems(snapshot);
			const completedIds = new Set(snapshot.completed_item_ids ?? []);
			completedIds.add(justCompletedItemId);
			const completedItemKind = items.find(
				(i) => i.id === justCompletedItemId,
			)?.kind;
			const recipientsFinalized =
				snapshot.recipients_finalized === true ||
				completedItemKind === 'recipients';

			const projectedSnapshot = {
				...snapshot,
				completed_item_ids: [...completedIds],
				recipients_finalized: recipientsFinalized,
			};

			const next = items.find((item) => {
				if (item.id === justCompletedItemId) return false;
				// A skipped artifact is neither completed nor a recipients/schedule
				// step, so without this it would be picked as the "next" step and its
				// editor would open right after the user skipped it.
				if (item.kind === 'artifact' && isSkipped(projectedSnapshot, item.id)) {
					return false;
				}
				if (item.kind === 'recipients') {
					return !recipientsFinalized;
				}
				if (item.kind === 'schedule') {
					return isProcessReadyToSchedule(projectedSnapshot);
				}
				return !completedIds.has(item.id);
			});

			if (!next) {
				bridgeApi.sendAction({ type: 'do_close_chat_panel' });
				return;
			}

			if (next.kind === 'recipients') {
				openChatPanel({
					panel_type: 'process_recipients',
					item_id: processId,
				});
				return;
			}

			if (next.kind === 'schedule') {
				openChatPanel({
					panel_type: 'process_schedule',
					item_id: processId,
				});
				return;
			}

			if (next.kind === 'artifact') {
				const artifact = snapshot.children.find((c) => c.id === next.id);
				openChatPanel({
					panel_type: 'artifact',
					item_id: next.id,
					...(artifact?.type && { artifact_type: artifact.type }),
				});
				return;
			}

			if (next.kind === 'action') {
				const action = snapshot.actions.find((a) => a.id === next.id);
				openChatPanel({
					panel_type: 'action',
					item_id: next.id,
					...(action?.type && { artifact_type: action.type }),
				});
			}
		},
		[bridgeApi, openChatPanel],
	);
};
